import { Permission } from '@hr-outsourcing/shared';
import { EntityId } from '@/types/common';
const PERMISSION_NAME_TO_ENUM: Record<string, Permission> = {
  'employees:create': Permission.EMPLOYEE_CREATE,
  'employees:update:all': Permission.EMPLOYEE_UPDATE_ALL,
  'employees:read:all': Permission.EMPLOYEE_READ_ALL,
  'employees:delete': Permission.EMPLOYEE_DELETE,
  'employees:read:salary': Permission.EMPLOYEE_READ_SALARY,
  'employees:update:salary': Permission.EMPLOYEE_UPDATE_SALARY,
  'candidates:create': Permission.CANDIDATE_CREATE,
  'candidates:update': Permission.CANDIDATE_UPDATE,
  'candidates:read': Permission.CANDIDATE_READ,
  'candidates:delete': Permission.CANDIDATE_DELETE,
  'positions:create': Permission.POSITION_CREATE,
  'positions:update': Permission.POSITION_UPDATE,
  'positions:read': Permission.POSITION_READ,
  'positions:delete': Permission.POSITION_DELETE,
  'titles:create': Permission.TITLE_CREATE,
  'titles:update': Permission.TITLE_UPDATE,
  'titles:read': Permission.TITLE_READ,
  'titles:delete': Permission.TITLE_DELETE,
  'skills:create': Permission.SKILL_CREATE,
  'skills:update': Permission.SKILL_UPDATE,
  'skills:read': Permission.SKILL_READ,
  'skills:delete': Permission.SKILL_DELETE,
  'companies:create': Permission.COMPANY_CREATE,
  'companies:update': Permission.COMPANY_UPDATE,
  'companies:read': Permission.COMPANY_READ,
  'companies:delete': Permission.COMPANY_DELETE,
  'leaves:approve': Permission.LEAVES_APPROVE,
  'leaves:read:all': Permission.LEAVES_READ_ALL,
  'roles:create': Permission.ROLES_CREATE,
  'roles:update': Permission.ROLES_UPDATE,
  'roles:read': Permission.ROLES_READ,
  'roles:delete': Permission.ROLES_DELETE,
  'customer:company:read': Permission.CUSTOMER_COMPANY_READ,
  'customer:company:manage': Permission.CUSTOMER_COMPANY_MANAGE,
  'announcements:manage': Permission.ANNOUNCEMENT_MANAGE,
  'announcements:company:manage': Permission.ANNOUNCEMENT_COMPANY_MANAGE,
  'expense:read:all': Permission.EXPENSE_READ_ALL,
  'expense:update:status': Permission.EXPENSE_UPDATE_STATUS,
  'expense:delete:all': Permission.EXPENSE_DELETE_ALL,
  'overtime:evaluate': Permission.OVERTIME_EVALUATE,
  'overtime:read:all': Permission.OVERTIME_READ_ALL,
  'overtime:manage:approved': Permission.OVERTIME_MANAGE_APPROVED,
  'interviews:manage': Permission.INTERVIEW_MANAGE,
  'interviews:create': Permission.INTERVIEW_CREATE,
  'interviews:read': Permission.INTERVIEW_READ,
  'leave_policy:manage': Permission.LEAVE_POLICY_MANAGE,
  'groups:create': Permission.GROUP_CREATE,
  'groups:manage': Permission.GROUP_MANAGE,
  'groups:read:all': Permission.GROUP_READ_ALL,
  'groups:delete': Permission.GROUP_DELETE,
  'groups:update': Permission.GROUP_UPDATE,
  'groups:members:add': Permission.GROUP_MEMBER_ADD,
  'groups:members:remove': Permission.GROUP_MEMBER_REMOVE,
  'groups:members:update': Permission.GROUP_MEMBER_UPDATE,
  'groups:members:read': Permission.GROUP_MEMBER_READ,
  'teams:create': Permission.TEAM_CREATE,
  'employee_manager:read': Permission.EMPLOYEE_MANAGER_READ,
  'employee_manager:assign': Permission.EMPLOYEE_MANAGER_ASSIGN,
  'employee_manager:remove': Permission.EMPLOYEE_MANAGER_REMOVE,
  'candidates:kvkk:manage': Permission.CANDIDATE_KVKK_MANAGE,
  'universities:create': Permission.UNIVERSITY_CREATE,
  'universities:read': Permission.UNIVERSITY_READ,
  'universities:update': Permission.UNIVERSITY_UPDATE,
  'universities:delete': Permission.UNIVERSITY_DELETE,
  'goals:read': Permission.GOAL_READ,
  'goals:create': Permission.GOAL_CREATE,
  'goals:update': Permission.GOAL_UPDATE,
  'goals:delete': Permission.GOAL_DELETE,
  'goals:key_result:approve': Permission.GOAL_KEY_RESULT_APPROVE,
  'goals:key_result:update': Permission.GOAL_KEY_RESULT_UPDATE,
  'clevel_hierarchy:manage': Permission.CLEVEL_HIERARCHY_MANAGE,
  'organization_hierarchy:manage': Permission.ORGANIZATION_HIERARCHY_MANAGE,
  'recruitment_report:read': Permission.RECRUITMENT_REPORT_READ,
  'employees:offboarding:manage': Permission.EMPLOYEE_OFFBOARDING_MANAGE,
  'account-settings:manage': Permission.ACCOUNT_SETTINGS_MANAGE,
  'inventory:read_all': Permission.INVENTORY_READ_ALL,
  'inventory:access:view': Permission.INVENTORY_ACCESS_VIEW,
  'inventory:create': Permission.INVENTORY_CREATE,
  'inventory:update': Permission.INVENTORY_UPDATE,
  'inventory:delete': Permission.INVENTORY_DELETE,
  'inventory:approve': Permission.INVENTORY_APPROVE,
  'job_listings:manage': Permission.JOB_LISTING_MANAGE,
  'job_applications:read': Permission.JOB_APPLICATION_READ,
  'job_applications:manage': Permission.JOB_APPLICATION_MANAGE,
  // Legacy key — still used by existing group permission rows in production.
  // Remove after the migration renames these rows in the next release.
  'read:recruitment-report': Permission.RECRUITMENT_REPORT_READ,
};
export function permissionNameToEnum(permissionName: string): Permission | null {
  return PERMISSION_NAME_TO_ENUM[permissionName] ?? null;
}
export interface Membership {
  groupId: EntityId;
  permissions: Array<{
    name: string;
  }>;
  teamPermissions?: Array<{
    teamId: EntityId;
    permissions: string[];
  }>;
  leadingTeams?: Array<{
    id: EntityId;
    name: string;
  }>;
}

const normalizeEntityId = (id: EntityId | null | undefined): string => String(id ?? '');

export function extractPermissionsFromMemberships(memberships: Membership[]): Permission[] {
  const permissionSet = new Set<Permission>();
  for (const membership of memberships) {
    for (const perm of membership.permissions) {
      const enumValue = permissionNameToEnum(perm.name);
      if (enumValue !== null) {
        permissionSet.add(enumValue);
      }
    }
    if (membership.teamPermissions) {
      for (const teamPerm of membership.teamPermissions) {
        for (const permName of teamPerm.permissions) {
          const enumValue = permissionNameToEnum(permName);
          if (enumValue !== null) {
            permissionSet.add(enumValue);
          }
        }
      }
    }
  }
  return Array.from(permissionSet);
}

export function getMembershipForGroup(
  memberships: Membership[] | null | undefined,
  groupId: EntityId | null | undefined
): Membership | null {
  if (!memberships?.length || groupId == null) return null;
  const normalizedGroupId = normalizeEntityId(groupId);
  return memberships.find((membership) => normalizeEntityId(membership.groupId) === normalizedGroupId) ?? null;
}

export function membershipHasPermission(membership: Membership | null | undefined, permission: Permission): boolean {
  if (!membership) return false;
  return membership.permissions.some((perm) => permissionNameToEnum(perm.name) === permission);
}

export function hasGroupScopedPermission(
  memberships: Membership[] | null | undefined,
  groupId: EntityId | null | undefined,
  permission: Permission
): boolean {
  return membershipHasPermission(getMembershipForGroup(memberships, groupId), permission);
}

export function getLeadingTeamIdsForGroup(
  memberships: Membership[] | null | undefined,
  groupId: EntityId | null | undefined
): Set<EntityId> {
  const membership = getMembershipForGroup(memberships, groupId);
  return new Set((membership?.leadingTeams ?? []).map((team) => team.id));
}
